import styled from '@emotion/styled';
import { forwardRef } from 'react';
import { colors, radii, breakpoints } from './tokens';

// Figma "Icon Button" (square, radius 5). Variants:
//   filled  - slate square (mic / camera toggles in the video controls row)
//   outline - white with slate border (device settings cog)
//   danger  - rose filled (a control that is currently OFF, e.g. muted mic)
//   header  - white outline on the teal header (hamburger menu)
//   teal    - small circular teal button (panel expand/collapse)
const STYLES = {
  filled: `background:${colors.controlSlate};color:#fff;border:1px solid ${colors.controlSlate};
    &:hover:not(:disabled){background:${colors.slate[600]};border-color:${colors.slate[600]};}`,
  outline: `background:#fff;color:${colors.slate[600]};border:1px solid ${colors.slate[400]};
    &:hover:not(:disabled){background:${colors.slate[50]};}`,
  danger: `background:${colors.error[500]};color:#fff;border:1px solid ${colors.error[500]};
    &:hover:not(:disabled){background:${colors.error[600]};border-color:${colors.error[600]};}`,
  header: `background:transparent;color:#fff;border:1px solid rgba(255,255,255,0.85);
    &:hover:not(:disabled){background:rgba(255,255,255,0.12);}`,
  teal: `background:${colors.teal};color:#fff;border:0;border-radius:50% !important;
    &:hover:not(:disabled){background:${colors.primary};}`,
  ghost: `background:transparent;color:${colors.slate[600]};border:0;
    &:hover:not(:disabled){background:${colors.slate[100]};}`,
};

const forward = (p) => !['variant', 'size', 'round', 'pressed'].includes(p);

const Root = styled('button', { shouldForwardProp: forward })`
  --ib-size: ${({ size }) => size}px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: var(--ib-size);
  height: var(--ib-size);
  padding: 0;
  border-radius: ${({ round }) => (round ? '50%' : `${radii.button + 3}px`)};
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: background-color 120ms ease, border-color 120ms ease;
  ${({ variant }) => STYLES[variant] || STYLES.filled}
  &:focus-visible {
    outline: 3px solid ${colors.brand[300]};
    outline-offset: 2px;
  }
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
  ${({ size }) => (size >= 44 ? `@media (max-width:${breakpoints.phone - 1}px){--ib-size:${Math.max(36, size - 6)}px;}` : '')}
`;

// `label` is required: it becomes aria-label and the tooltip.
export const IconButton = forwardRef(({ label, variant = 'filled', size = 44, round = false, pressed, children, type = 'button', ...rest }, ref) => (
  <Root
    ref={ref}
    type={type}
    aria-label={label}
    title={label}
    aria-pressed={pressed === undefined ? undefined : Boolean(pressed)}
    variant={variant}
    size={size}
    round={round}
    {...rest}
  >
    {children}
  </Root>
));
IconButton.displayName = 'IconButton';

export default IconButton;
